import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, Users, Heart, LogOut, Inbox } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { TIERS } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "sa_admin_key";

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [partners, setPartners] = useState([]);

  const headers = { "X-Admin-Key": key };

  const login = async (e) => {
    e?.preventDefault();
    if (!key) return toast.error("Enter the admin key");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers });
      localStorage.setItem(STORAGE_KEY, key);
      setAuthed(true);
      await Promise.all([loadContacts(), loadPartners()]);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid admin key");
      localStorage.removeItem(STORAGE_KEY);
      setAuthed(false);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async () => {
    const r = await axios.get(`${API}/contact`, { headers });
    setContacts(r.data || []);
  };
  const loadPartners = async () => {
    const r = await axios.get(`${API}/partnership`, { headers });
    setPartners(r.data || []);
  };

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
    setKey("");
    setContacts([]);
    setPartners([]);
  };

  // Auto-attempt login if key cached
  React.useEffect(() => {
    if (key && !authed) {
      (async () => {
        try {
          await axios.post(`${API}/admin/verify`, {}, { headers });
          setAuthed(true);
          loadContacts(); loadPartners();
        } catch {}
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <div data-testid="page-admin-login" className="min-h-[70vh] flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-md rounded-3xl bg-white border border-black/5 p-8">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-[#1F2937] text-white flex items-center justify-center">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-heading text-xl font-semibold text-[#1F2937]">Admin Sign-In</h1>
              <p className="text-sm text-[#6B7280]">View contact & partnership submissions.</p>
            </div>
          </div>
          <label className="block mt-6 text-xs uppercase tracking-wider font-semibold text-[#6B7280]">Admin Key</label>
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="mt-2 w-full rounded-2xl bg-[#F4F1EA] text-[#1F2937] px-4 py-3 text-sm border border-black/5 focus:outline-none focus:ring-2 focus:ring-[#2196F3]"
            placeholder="Enter admin key"
          />
          <button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading}
            className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#2196F3] hover:bg-[#1e88e5] text-white px-6 py-3 text-sm font-semibold disabled:opacity-60"
          >
            {loading ? "Verifying…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  const tierName = (k) => TIERS.find((t) => t.key === k)?.name || (k === "custom" ? "Custom" : k);
  const tierColor = (k) => TIERS.find((t) => t.key === k)?.color || "#1F2937";

  return (
    <div data-testid="page-admin">
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-6 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Admin</div>
          <h1 className="mt-2 font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F2937]">Submissions</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-btn"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 hover:border-black/30 text-[#1F2937] px-5 py-2.5 text-sm font-semibold">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-8">
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Stat label="Contact messages" value={contacts.length} icon={Mail} color="#2196F3" />
          <Stat label="Partnership inquiries" value={partners.length} icon={Heart} color="#E53935" />
          <Stat label="All submissions" value={contacts.length + partners.length} icon={Inbox} color="#FFB300" />
        </div>

        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="bg-white border border-black/5 rounded-full p-1">
            <TabsTrigger value="contacts" data-testid="admin-tab-contacts" className="rounded-full data-[state=active]:bg-[#1F2937] data-[state=active]:text-white">Contacts ({contacts.length})</TabsTrigger>
            <TabsTrigger value="partners" data-testid="admin-tab-partners" className="rounded-full data-[state=active]:bg-[#1F2937] data-[state=active]:text-white">Partnerships ({partners.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="contacts" className="mt-6">
            <div className="rounded-3xl bg-white border border-black/5 overflow-hidden">
              {contacts.length === 0 ? (
                <Empty message="No contact submissions yet." />
              ) : (
                <table className="w-full text-sm" data-testid="admin-contacts-table">
                  <thead className="bg-[#F4F1EA] text-[#6B7280] text-xs uppercase tracking-wider">
                    <tr>
                      <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Phone</Th><Th>Subject</Th><Th>Message</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} className="border-t border-black/5 align-top">
                        <Td>{new Date(c.created_at).toLocaleString()}</Td>
                        <Td className="font-medium text-[#1F2937]">{c.name}</Td>
                        <Td><a className="text-[#2196F3] hover:underline" href={`mailto:${c.email}`}>{c.email}</a></Td>
                        <Td>{c.phone || "—"}</Td>
                        <Td>{c.subject || "—"}</Td>
                        <Td className="max-w-md whitespace-pre-wrap">{c.message}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="partners" className="mt-6">
            <div className="rounded-3xl bg-white border border-black/5 overflow-hidden">
              {partners.length === 0 ? (
                <Empty message="No partnership inquiries yet." />
              ) : (
                <table className="w-full text-sm" data-testid="admin-partners-table">
                  <thead className="bg-[#F4F1EA] text-[#6B7280] text-xs uppercase tracking-wider">
                    <tr>
                      <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Org</Th><Th>Tier</Th><Th>Phone</Th><Th>Note</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {partners.map((p) => (
                      <tr key={p.id} className="border-t border-black/5 align-top">
                        <Td>{new Date(p.created_at).toLocaleString()}</Td>
                        <Td className="font-medium text-[#1F2937]">{p.name}</Td>
                        <Td><a className="text-[#2196F3] hover:underline" href={`mailto:${p.email}`}>{p.email}</a></Td>
                        <Td>{p.organization || "—"}</Td>
                        <Td>
                          <span className="inline-flex items-center gap-1.5 rounded-full text-xs font-semibold px-2.5 py-1"
                                style={{ backgroundColor: `${tierColor(p.tier)}15`, color: tierColor(p.tier), border: `1px solid ${tierColor(p.tier)}33` }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: tierColor(p.tier) }} />
                            {tierName(p.tier)}
                          </span>
                        </Td>
                        <Td>{p.phone || "—"}</Td>
                        <Td className="max-w-md whitespace-pre-wrap">{p.message || "—"}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

const Th = ({ children }) => <th className="text-left font-semibold px-4 py-3">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 text-[#4B5563] ${className}`}>{children}</td>;

const Stat = ({ label, value, icon: Icon, color }) => (
  <div className="rounded-3xl bg-white border border-black/5 p-6 flex items-center gap-4">
    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}15`, border: `1px solid ${color}33` }}>
      <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.6} />
    </div>
    <div>
      <div className="font-heading text-3xl font-bold text-[#1F2937]">{value}</div>
      <div className="text-sm text-[#6B7280]">{label}</div>
    </div>
  </div>
);

const Empty = ({ message }) => (
  <div className="p-12 text-center text-[#6B7280] flex flex-col items-center gap-3">
    <Users className="w-7 h-7" strokeWidth={1.4} />
    <div>{message}</div>
  </div>
);
